import { Component, OnInit } from '@angular/core';
import * as Auth from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../services/api/api.service';

enum PageState {
  LOGIN = 'login',
  CONFIRM_OAUTH = 'confirm_oauth',
}

const SESSION_STORAGE_OAUTH_PARAMS = 'google_oauth_query_params';

@Component({
  selector: 'app-login',
  imports: [CommonModule, FormsModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  standalone: true,
})
export class LoginComponent implements OnInit {
  private queryClientId: string;
  private queryRedirectUri: string;
  private queryResponseType: string;
  private queryScope: string;
  private queryState: string;

  public email: string = '';
  public password: string = '';

  public errorMessage: string = '';

  public pageState: PageState = PageState.LOGIN;
  public pageStateEnum = PageState;

  constructor(
    private activatedRoute: ActivatedRoute,
    private apiService: ApiService
  ) {
    // Extract query parameters from the URL, which are sent from Google OAuth 2.0
    this.queryClientId =
      this.activatedRoute.snapshot.queryParamMap.get('client_id') || '';
    this.queryRedirectUri =
      this.activatedRoute.snapshot.queryParamMap.get('redirect_uri') || '';
    this.queryResponseType =
      this.activatedRoute.snapshot.queryParamMap.get('response_type') || '';
    this.queryScope =
      this.activatedRoute.snapshot.queryParamMap.get('scope') || '';
    this.queryState =
      this.activatedRoute.snapshot.queryParamMap.get('state') || '';

    // If the URL has no Google OAuth params it likely means we just came back
    // from a social login redirect. Restore the params previously persisted in
    // sessionStorage so confirmOAuth() can still call getAuthToken correctly.
    if (!this.queryClientId) {
      const cached = sessionStorage.getItem(SESSION_STORAGE_OAUTH_PARAMS);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          this.queryClientId = parsed.client_id || '';
          this.queryRedirectUri = parsed.redirect_uri || '';
          this.queryResponseType = parsed.response_type || '';
          this.queryScope = parsed.scope || '';
          this.queryState = parsed.state || '';
        } catch (_) {
          // ignore parse error, fall back to empty params
        }
      }
    }

    console.log('Query Parameters:', {
      clientId: this.queryClientId,
      redirectUri: this.queryRedirectUri,
      responseType: this.queryResponseType,
      scope: this.queryScope,
      state: this.queryState,
    });
  }

  async ngOnInit(): Promise<void> {
    // If sessionStorage holds the OAuth params it means we just came back from
    // a social login redirect. Amplify's OAuth listener completes the token
    // exchange asynchronously, so wait for the Hub `signedIn` event before
    // calling getCurrentUser (otherwise we hit a race and fall back to LOGIN).
    const returningFromSocial = !!sessionStorage.getItem(SESSION_STORAGE_OAUTH_PARAMS)
      && !this.activatedRoute.snapshot.queryParamMap.get('client_id');

    if (returningFromSocial) {
      await this.waitForSocialSignIn(15000);
    }

    try {
      const user = await Auth.getCurrentUser();
      if (user) {
        const session = await Auth.fetchAuthSession();

        // signInDetails.loginId is populated for username/password sign-in;
        // for social sign-in fall back to the email claim from the id token.
        this.email = session.tokens?.signInDetails?.loginId
          || (session.tokens?.idToken?.payload?.['email'] as string)
          || '';

        // If a user is already logged in, change the page state to CONFIRM_OAUTH
        this.pageState = PageState.CONFIRM_OAUTH;
      }
    } catch (error: any) {
      // If any error occurs during initialization, set the page state to LOGIN
      this.pageState = PageState.LOGIN;
    }
  }

  private waitForSocialSignIn(timeoutMs: number): Promise<void> {
    return new Promise<void>((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        unsubscribe();
        clearTimeout(timer);
        resolve();
      };
      const unsubscribe = Hub.listen('auth', ({ payload }) => {
        if (payload.event === 'signedIn' || payload.event === 'signInWithRedirect') {
          finish();
        }
        if (payload.event === 'signInWithRedirect_failure') {
          this.errorMessage = 'Social sign-in failed';
          sessionStorage.removeItem(SESSION_STORAGE_OAUTH_PARAMS);
          finish();
        }
      });
      const timer = setTimeout(finish, timeoutMs);
    });
  }

  async login() {
    this.errorMessage = '';

    try {
      const signInStatus = await Auth.signIn({
        username: this.email,
        password: this.password,
      });

      if (signInStatus.isSignedIn == true) {
        // Change page state to confirm OAuth
        this.pageState = PageState.CONFIRM_OAUTH;
      } else {
        this.errorMessage = 'Invalid email or password';
      }
    } catch (error: any) {
      this.errorMessage = error.message || 'Unknown error';
    }
  }

  async signOut() {
    try {
      await Auth.signOut();

      // Reset email and password fields
      this.email = '';
      this.password = '';
      this.errorMessage = '';

      this.pageState = PageState.LOGIN;
    } catch (error: any) {
      this.errorMessage = error.message || 'Error signing out';
    }
  }

  async socialLogin(provider: 'GOOGLE' | 'APPLE') {
    this.errorMessage = '';

    // Persist the Google OAuth query params before leaving the page so we can
    // restore them after the social login redirects us back here.
    sessionStorage.setItem(
      SESSION_STORAGE_OAUTH_PARAMS,
      JSON.stringify({
        client_id: this.queryClientId,
        redirect_uri: this.queryRedirectUri,
        response_type: this.queryResponseType,
        scope: this.queryScope,
        state: this.queryState,
      })
    );

    try {
      const amplifyProvider = provider === 'APPLE' ? 'Apple' : 'Google';
      await Auth.signInWithRedirect({ provider: amplifyProvider });
    } catch (error: any) {
      this.errorMessage = error.message || `Failed to sign in with ${provider}`;
      sessionStorage.removeItem(SESSION_STORAGE_OAUTH_PARAMS);
    }
  }

  get hasOAuthRequest(): boolean {
    return !!this.queryClientId && !!this.queryRedirectUri && !!this.queryState && !!this.queryResponseType;
  }

  async confirmOAuth() {
    if (!this.hasOAuthRequest) {
      this.errorMessage = 'No OAuth flow in progress. Open this page from a Google Smart Home account-linking request.';
      return;
    }
    // Implement OAuth confirmation logic here
    let response = await this.apiService.getAuthToken({
      client_id: this.queryClientId!,
      redirect_uri: this.queryRedirectUri!,
      state: this.queryState!,
      response_type: this.queryResponseType!,
    });

    if (response && response.redirectUrl) {
      sessionStorage.removeItem(SESSION_STORAGE_OAUTH_PARAMS);
      // Redirect to the OAuth provider
      window.location.href = response.redirectUrl;
    } else {
      this.errorMessage = 'Failed to get authorization token';
    }
  }

  async cancelOAuth() {
    sessionStorage.removeItem(SESSION_STORAGE_OAUTH_PARAMS);
    // Implement OAuth cancellation logic here
    await this.signOut();
  }
}
